<template>
  <div v-if="report_data" id="ttable" ref="report_wrapper">
    <!-- report -->
    <b-card>
      <div v-if="loading || dataTable_loading" class="loading_page">
        <orbit-spinner :animation-duration="1000" :size="100" :color="'#0e96b1'" />
      </div>

      <b-row>

        <b-col cols="2" md="2" class="d-flex align-items-center justify-content-start mb-1 mb-md-0">
          <label>{{ $t('Show') }}</label>
          <v-select v-model="request.length" :dir="$store.getters['auth/preferDirIsRtl'] ? 'rtl' : 'ltr'"
            :options="perPageOptions" :clearable="false" v-on:input="getReport"
            class="per-page-selector d-inline-block mx-50" />
          <label>{{ $t('entries') }}</label>
        </b-col>
        <b-col cols="6" md="6">
          <b-form-group :label="$t('Filter')" label-cols-sm="3" label-align-sm="right" label-size="sm"
            label-for="filterInput" class="mb-0">
            <b-input-group size="md">
              <b-form-input id="filterInput" v-model="filter" type="search" :placeholder="$t('Type to Search')" />
              <b-input-group-append>
                <b-button :disabled="!filter" @click="filter = ''">
                  {{ $t('Clear') }}
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>

        <b-col cols="4" md="4" class="d-flex align-items-center justify-content-start mb-1 mb-md-0">

          <div class="col-12 mb-1 d-flex justify-content-end">
            <b-button v-if="report_data" v-ripple.400="'rgba(255, 255, 255, 0.15)'" @click.prevent="Print" type="button"
              variant="primary" class="mr-1 btn">
              {{ $t('print') }}
            </b-button>
            <b-button v-if="report_data" v-ripple.400="'rgba(255, 255, 255, 0.15)'" @click.prevent="Export('pdf')"
              type="button" variant="primary" class="mr-1 btn">
              {{ $t('pdf') }}
            </b-button>

            <b-button v-if="report_data" v-ripple.400="'rgba(255, 255, 255, 0.15)'" @click.prevent="Export('xlsx')"
              type="button" variant="primary" class="mr-1 btn">
              {{ $t('Excel') }}
            </b-button>
          </div>

        </b-col>


        <b-col cols="12" class="mt-1">
          <b-table :tbody-tr-class="getRowClass" small ref="tableRef" class="position-relative" :items="report_data"
            responsive :filter="filter"
            :fields="report_fields.map((i) => ({ key: i.key, label: $t(i.label), sortable: i.sortable }))" show-empty
            empty-text="No matching records found" :sort-desc.sync="isSortDirDesc" :sort-by.sync="sortBy"
            :sortData="sortData" no-local-sorting>


            <template #cell(index)="data">
              {{ data.index + 1 }}
            </template>
            <template #cell(times)="data">
              <span v-b-tooltip.hover.right="$t(data.item.times)"> {{ $t(data.item.times) | str_limit(15) }}</span>
            </template>

            <template #cell(device_category)="data">
              {{ $t(data.item.device_category) }}
            </template>

            <template #cell(durations)="data">
              <span v-b-tooltip.hover.right="$t(data.item.durations)">{{ $t(data.item.durations) | str_limit(15) }}
              </span>
            </template>

            <template #cell(percent)="data">
              <span>
                {{ data.item.percent + "%" }}
              </span>
            </template>

            <template #cell(type)="data">
              <span>
                {{ $t(data.item.type) }}
              </span>
            </template>

            <template #cell(date)="data" style="color:red">
              <div v-if="Array.isArray(data.item.date)">
                <div v-if="data.item.day.includes('|')">
                  <span style="margin: 0.2rem" pill v-for="date in data.item.date.split('|')" v-bind:key="date.id">
                    {{ date }}
                  </span>
                </div>
              </div>

              <div v-if="data.item.date !== undefined && !data.item.date.includes('|')">
                {{ $t(data.item.date) }}
              </div>

            </template>

            <template #cell(day)="data" style="color:red">
              <div v-if="Array.isArray(data.item.day)">
                <div v-if="data.item.day.includes('|')">
                  <span style="margin: 0.2rem" pill v-for="day in data.item.day.split('|')" v-bind:key="day.id">
                    {{ $t(day) }}
                  </span>
                </div>
              </div>

              <div v-if="!data.item.day.includes('|')">

                {{ $t(data.item.day) }}
              </div>

            </template>

            <template #cell(location)="data">
              <span>
                <a target="_blank"  :href="`https://maps.google.com/maps?q=${data.item.location}`">{{ data.item.location }}</a>
              </span>
            </template>

          </b-table>

          <div class="mx-4 mb-2">
            <b-row>
              <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-start">
                <span class="text-muted">{{ $t('Showing') }} {{ from }} to {{ to }} of {{ total }}
                  {{ $t('entries') }}</span>
              </b-col>
              <!-- Pagination -->

              <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-end">
                <b-pagination v-model="request.page" :total-rows="total" :per-page="request.length" v-on:input="getReport"
                  first-number last-number class="mb-0 mt-1 mt-sm-0" prev-class="prev-item" next-class="next-item">
                  <template #prev-text>
                    <feather-icon icon="ChevronLeftIcon" size="18" />
                  </template>
                  <template #next-text>
                    <feather-icon icon="ChevronRightIcon" size="18" />
                  </template>
                </b-pagination>
              </b-col>
            </b-row>
          </div>
        </b-col>
      </b-row>
    </b-card>
  </div>
</template>

<script>
import { ref, onUpdated, watch } from '@vue/composition-api'
import Ripple from 'vue-ripple-directive'
import { OrbitSpinner } from 'epic-spinners'
import vSelect from 'vue-select'
import { ExportDataServices } from '@/share/reports/exportService'
import useHelpers from '@/views/modules/useHelpers'
import {
  BButton, BTable, BFormInvalidFeedback, BCard, BCol, BFormCheckboxGroup,
  BForm, BFormCheckbox, BFormRadioGroup, BFormGroup, BFormInput, BPagination,
  BInputGroup, BInputGroupPrepend, BRow, BAlert, BTabs, BTab, BCardText, VBTooltip,
  BInputGroupAppend, BBadge
} from 'bootstrap-vue'
export default {
  components: {
    BRow, BBadge,
    BCard, BFormCheckboxGroup, BTable, BPagination,
    BCol, BFormRadioGroup, BFormInvalidFeedback,
    BAlert,
    BFormGroup,
    BFormInput,
    BFormCheckbox,
    BInputGroup,
    BInputGroupPrepend,
    BForm,
    BButton,
    BTabs,
    BCardText,
    BTab,
    OrbitSpinner,
    BInputGroupAppend,
    vSelect
  },
  directives: {
    Ripple, 'b-tooltip': VBTooltip,
  },

  props: ['report_data', 'report_fields', 'filter_included_fields', 'request', 'errors', 'loading', 'getReport', 'total', 'from', 'to', 'sortData'],


  setup(props, { root, emit }) {
    const sortBy = ref('id')
    const isSortDirDesc = ref(true)
    const index_column = ref(0)
    const { toaster } = useHelpers(root)
    const filterOn = ref([]);
    const dataTable_loading = ref(false);
    const perPageOptions = [5, 10, 25, 50, 100, 500, 1000]
    watch([sortBy, isSortDirDesc], () => {

      props.sortData({ 'sortBy': sortBy.value, 'isSortDirDesc': isSortDirDesc.value })

    })

    const Export = (fileType) => {

      let req = props.request;
      dataTable_loading.value = true;


      //let reportsSupportNewExport=['trip','distance','pestPlan','absence','compactor','containerPlan','drainagePlan','fillAreas','forbiddenAreas','gardenPlan','geofence','history','networkPlan','rainPlan','status','supportCenter','supportCompactor','supportDevices','sweepPlan','sweep','utility'];

      Object.assign(req, { exportType: fileType })
      Object.assign(req, { exporter: fileType, export: req.type + "Report" })
      ExportDataServices.exportReport(req).
        then(
          (res) => { dataTable_loading.value = false }

        ).catch(reason => {
          console.log('Export faild')
          dataTable_loading.value = false
          if (reason.status === 403) {
            toaster('downoald faild', 'warning', root.$i18n.t('you have no permission'))
          } else {
            toaster('downoald faild', 'danger', root.$i18n.t('Error'))
          }
        })



    }

    const Print = () => {
      dataTable_loading.value = true;
      let req = props.request;
      // Object.assign(req, { exportType: "pdf"  })
      Object.assign(req, { exporter: "pdf", export: req.type + "Report" })
      delete req.page;
      delete req.length;
      // delete req.type
      ExportDataServices.printReport(req).
        then(
          (res) => { dataTable_loading.value = false }
        ).catch(reason => {
          console.log('print faild')
          dataTable_loading.value = false
          if (reason.status === 403) {
            toaster('downoald faild', 'warning', root.$i18n.t('you have no permission'))
          } else {
            toaster('downoald faild', 'danger', root.$i18n.t('Error'))
          }
        })







    }
    const getRowClass = (item, r) => {

      if (item && r === 'row') {
        return item.groupRow ? 'group-row' : ''
      } else {
        return null
      }
    }


    const filter = ref(null);

    return {
      perPageOptions,
      dataTable_loading,
      getRowClass,
      filter,
      filterOn,
      Print,
      Export,
      index_column,
      isSortDirDesc, sortBy

    }
  }
}
</script>

<style lang="scss" scoped>
.group-row {
  background-color: #ddd;
}

.tooltip {

  font-size: 1.2rem;
}
</style>